function currency(e: string | number | null | undefined) {
  // Formats input as BRL string without currency symbol (e.g. "12.547,42")
  // Accepts numbers, digits-only strings (cents), API dot-decimal strings,
  // and already formatted pt-BR strings.
  if (e === null || e === undefined) return '0,00'

  let input: string = typeof e === 'number' ? e.toFixed(2) : e

  // API sends dot-decimal strings ("12.5", "100.0", "1234.56") — convert to cents
  // before stripping non-digits so we don't lose magnitude.
  if (typeof input === 'string' && /^-?\d+\.\d+$/.test(input.trim())) {
    input = (parseFloat(input) * 100).toFixed(0)
  }

  let value = String(input).replace(/\D/g, '')
  value = value.padStart(3, '0')

  value = value.replace(/(\d)(\d{2})$/, '$1,$2')
  value = value.replace(/(?=(\d{3})+(\D))\B/g, '.')

  return value
}

export function parseMoney(value: unknown): number | null {
  if (value === null || value === undefined) return null

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }

  if (typeof value !== 'string') return null

  const raw = value.trim()
  if (!raw) return null

  // Keep only digits and separators
  let cleaned = raw.replace(/[^\d.,-]/g, '')
  cleaned = cleaned.replace(/(?!^)-/g, '') // keep only leading '-'

  if (!cleaned) return null

  // Digits-only strings come from masked inputs (cents)
  if (/^-?\d+$/.test(cleaned)) {
    const n = Number(cleaned)
    return Number.isFinite(n) ? n / 100 : null
  }

  const hasComma = cleaned.includes(',')
  const hasDot = cleaned.includes('.')

  let normalized = cleaned

  if (hasComma && hasDot) {
    // pt-BR: "." thousands and "," decimal
    normalized = cleaned.replace(/\./g, '').replace(',', '.')
  } else if (hasComma && !hasDot) {
    // If multiple commas, treat the last as decimal separator
    const lastComma = cleaned.lastIndexOf(',')
    normalized =
      cleaned.slice(0, lastComma).replace(/,/g, '') +
      '.' +
      cleaned.slice(lastComma + 1)
  } else if (!hasComma && hasDot) {
    // If multiple dots, treat the last as decimal separator
    const lastDot = cleaned.lastIndexOf('.')
    normalized =
      cleaned.slice(0, lastDot).replace(/\./g, '') +
      '.' +
      cleaned.slice(lastDot + 1)
  }

  const n = Number(normalized)
  return Number.isFinite(n) ? n : null
}

const formatMoney = (value: string | number | undefined) => {
  if (!value) return 'R$ 0,00'
  return Intl.NumberFormat('pt-BR', {
    currency: 'BRL',
    style: 'currency'
  }).format(Number(value))
}

function onlyNumber(e: string) {
  return e.replace(/\D/g, '')
}

// currency() formats with pt-BR thousands separators ("1.234.567,89"), so to
// get back to a JS number every "." (thousands) must be stripped, not just the
// first one, otherwise values >= 1.000.000 become NaN.
function formatMoneyToRequest(amount: string): number {
  return parseMoney(amount) ?? 0
}

const priceConverter = (value: unknown): number => {
  return parseMoney(value) ?? 0
}

// Formata um número com sinal negativo antes do símbolo ("- R$ 1.234,56"),
// usado nas tabelas de Fluxo de Caixa e DRE para alinhar a leitura de saídas.
const formatCurrencySigned = (value: number): string => {
  // Protege contra NaN/Infinity (ex.: Number(campo_ausente)) para não
  // renderizar "R$ NaN" nas tabelas de DRE e Fluxo de Caixa.
  const safeValue = Number.isFinite(value) ? value : 0
  const absValue = Math.abs(safeValue)
  return `${safeValue < 0 ? '- ' : ''}R$ ${absValue.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
}

export {
  currency,
  formatMoney,
  onlyNumber,
  formatMoneyToRequest,
  priceConverter,
  formatCurrencySigned
}
